import { useLanguage } from "@/common/hooks/pages.hooks";
import { PRO_PROJECTS_PAGE_SIZE } from "@/features/pro-buyer-projects/static/pro-buyer-projects.static";
import {
  MutationHookOptions,
  QueryHookOptions,
  useMutation,
  useQuery,
} from "@apollo/client";
import assignPurchaseGql from "@whoppah/apollo/mutation/pro-projects/assignPurchaseToProBuyerProject.graphql";
import createProjectGql from "@whoppah/apollo/mutation/pro-projects/createProBuyerProject.graphql";
import deleteProjectGql from "@whoppah/apollo/mutation/pro-projects/deleteProBuyerProject.graphql";
import removePurchaseGql from "@whoppah/apollo/mutation/pro-projects/removePurchaseFromProBuyerProject.graphql";
import renameProjectGql from "@whoppah/apollo/mutation/pro-projects/renameProBuyerProject.graphql";
import requestForProjectGql from "@whoppah/apollo/mutation/pro-projects/requestReservationForProject.graphql";
import myReservationProjectsGql from "@whoppah/apollo/query/pro-projects/myReservationProjects.graphql";
import projectGql from "@whoppah/apollo/query/pro-projects/proBuyerProject.graphql";
import assignmentsGql from "@whoppah/apollo/query/pro-projects/proBuyerProjectAssignments.graphql";
import purchaseOptionsGql from "@whoppah/apollo/query/pro-projects/proBuyerProjectPurchaseOptions.graphql";
import projectsGql from "@whoppah/apollo/query/pro-projects/proBuyerProjects.graphql";
import overviewGql from "@whoppah/apollo/query/pro-projects/proBuyerProjectsOverview.graphql";
import {
  AssignPurchaseToProBuyerProjectMutation,
  AssignPurchaseToProBuyerProjectMutationVariables,
  CreateProBuyerProjectMutation,
  CreateProBuyerProjectMutationVariables,
  DeleteProBuyerProjectMutation,
  DeleteProBuyerProjectMutationVariables,
  MyReservationProjectsQuery,
  ProBuyerProjectAssignmentsQuery,
  ProBuyerProjectAssignmentsQueryVariables,
  ProBuyerProjectPurchaseOptionsQuery,
  ProBuyerProjectPurchaseOptionsQueryVariables,
  ProBuyerProjectQuery,
  ProBuyerProjectQueryVariables,
  ProBuyerProjectsOverviewQuery,
  ProBuyerProjectsQuery,
  ProBuyerProjectsQueryVariables,
  RemovePurchaseFromProBuyerProjectMutation,
  RemovePurchaseFromProBuyerProjectMutationVariables,
  RenameProBuyerProjectMutation,
  RenameProBuyerProjectMutationVariables,
  RequestReservationForProjectMutation,
  RequestReservationForProjectMutationVariables,
} from "@whoppah/apollo/schema";

/**
 * WHO-5959 6b. Pro Buyer projects over the gateway. Every mutation refetches
 * the overview figures, the first page and the picker options, so totals and
 * "in project X" labels never come from a hand-patched cache.
 */
const FIRST_PAGE = { limit: PRO_PROJECTS_PAGE_SIZE, offset: 0 };

/** Operation name: refetches the Aankopen chips (6c) only where that query is on screen. */
const ASSIGNMENTS_QUERY_NAME = "ProBuyerProjectAssignments";

/**
 * WHO-5959 F13: purchase titles are the locale's title (original as
 * fallback), as in Aankopen, so every query that selects them takes `lang`.
 */
const useTitleLanguage = () => useLanguage().language;

const useRefetchAfterChange = () => {
  const lang = useTitleLanguage();

  return [
    { query: projectsGql, variables: FIRST_PAGE },
    { query: overviewGql },
    { query: purchaseOptionsGql, variables: { lang } },
    ASSIGNMENTS_QUERY_NAME,
  ];
};

const useRefetchWithProject = (projectId: string | undefined) => {
  const lang = useTitleLanguage();
  const base = useRefetchAfterChange();

  return projectId
    ? [...base, { query: projectGql, variables: { id: projectId, lang } }]
    : base;
};

export { useProBuyerProjectsAccess } from "@/Apollo/hooks/useProBuyerProjectsAccess";

/** First page of 24; `fetchMore` with the next offset appends ("Meer laden"). */
export const useProBuyerProjectsList = (
  options?: QueryHookOptions<
    ProBuyerProjectsQuery,
    ProBuyerProjectsQueryVariables
  >
) =>
  useQuery<ProBuyerProjectsQuery, ProBuyerProjectsQueryVariables>(projectsGql, {
    fetchPolicy: "cache-and-network",
    variables: FIRST_PAGE,
    ...options,
  });

export const useProBuyerProjectsOverview = (
  options?: QueryHookOptions<ProBuyerProjectsOverviewQuery>
) =>
  useQuery<ProBuyerProjectsOverviewQuery>(overviewGql, {
    fetchPolicy: "cache-and-network",
    ...options,
  });

export const useProBuyerProject = (
  options: QueryHookOptions<ProBuyerProjectQuery, ProBuyerProjectQueryVariables>
) => {
  const lang = useTitleLanguage();

  return useQuery<ProBuyerProjectQuery, ProBuyerProjectQueryVariables>(
    projectGql,
    {
      fetchPolicy: "cache-and-network",
      ...options,
      variables: {
        lang,
        ...options.variables,
      } as ProBuyerProjectQueryVariables,
    }
  );
};

export const useProBuyerProjectPurchaseOptions = (
  options?: QueryHookOptions<
    ProBuyerProjectPurchaseOptionsQuery,
    ProBuyerProjectPurchaseOptionsQueryVariables
  >
) => {
  const lang = useTitleLanguage();

  return useQuery<
    ProBuyerProjectPurchaseOptionsQuery,
    ProBuyerProjectPurchaseOptionsQueryVariables
  >(purchaseOptionsGql, {
    fetchPolicy: "network-only",
    ...options,
    variables: { lang, ...options?.variables },
  });
};

/** WHO-5959 6c: the project of each listed purchase (Aankopen chip, menu and detail panel). */
export const useProBuyerProjectAssignments = (
  options: QueryHookOptions<
    ProBuyerProjectAssignmentsQuery,
    ProBuyerProjectAssignmentsQueryVariables
  >
) =>
  useQuery<
    ProBuyerProjectAssignmentsQuery,
    ProBuyerProjectAssignmentsQueryVariables
  >(assignmentsGql, {
    fetchPolicy: "cache-and-network",
    ...options,
  });

export const useCreateProBuyerProject = (
  options?: MutationHookOptions<
    CreateProBuyerProjectMutation,
    CreateProBuyerProjectMutationVariables
  >
) => {
  const refetchQueries = useRefetchAfterChange();

  return useMutation(createProjectGql, {
    ...options,
    awaitRefetchQueries: true,
    refetchQueries,
  });
};

export const useRenameProBuyerProject = (
  options?: MutationHookOptions<
    RenameProBuyerProjectMutation,
    RenameProBuyerProjectMutationVariables
  >
) => {
  const refetchQueries = useRefetchAfterChange();

  return useMutation(renameProjectGql, {
    ...options,
    awaitRefetchQueries: true,
    refetchQueries,
  });
};

export const useDeleteProBuyerProject = (
  options?: MutationHookOptions<
    DeleteProBuyerProjectMutation,
    DeleteProBuyerProjectMutationVariables
  >
) => {
  const refetchQueries = useRefetchAfterChange();

  return useMutation(deleteProjectGql, {
    ...options,
    awaitRefetchQueries: true,
    refetchQueries,
  });
};

/** `projectId` refetches that project page too; the mutation's own projectId is the target. */
export const useAssignPurchaseToProBuyerProject = (
  projectId?: string,
  options?: MutationHookOptions<
    AssignPurchaseToProBuyerProjectMutation,
    AssignPurchaseToProBuyerProjectMutationVariables
  >
) => {
  const refetchQueries = useRefetchWithProject(projectId);

  return useMutation(assignPurchaseGql, {
    ...options,
    awaitRefetchQueries: true,
    refetchQueries,
  });
};

export const useRemovePurchaseFromProBuyerProject = (
  projectId: string,
  options?: MutationHookOptions<
    RemovePurchaseFromProBuyerProjectMutation,
    RemovePurchaseFromProBuyerProjectMutationVariables
  >
) => {
  const refetchQueries = useRefetchWithProject(projectId);

  return useMutation(removePurchaseGql, {
    ...options,
    awaitRefetchQueries: true,
    refetchQueries,
  });
};

/** WHO-5959 6e: the project of each of the viewer's live reservations (Dashboard, spec S9). */
export const useMyReservationProjects = (
  options?: QueryHookOptions<MyReservationProjectsQuery>
) =>
  useQuery<MyReservationProjectsQuery>(myReservationProjectsGql, {
    fetchPolicy: "cache-and-network",
    ...options,
  });

/**
 * WHO-5959 6e: a reservation request for one of the viewer's projects (spec
 * S8). Refetches the overview and the first page, so the project moves up
 * (last activity) and its card chip and the Gereserveerd tile update.
 */
export const useRequestReservationForProject = (
  options?: MutationHookOptions<
    RequestReservationForProjectMutation,
    RequestReservationForProjectMutationVariables
  >
) =>
  useMutation(requestForProjectGql, {
    ...options,
    refetchQueries: [
      { query: projectsGql, variables: FIRST_PAGE },
      { query: overviewGql },
    ],
  });
